import { BehaviorSubject, Subject } from 'rxjs';

import { Platform } from '@angular/cdk/platform';

import { FsDataDogRumService } from '@lib/data-dog-rum';
import { SnPlayerState } from '@lib/player/common';

import { SnVideoPlayerType } from '../video-player.enums';
import { SnVideoPlayerReference } from '../video-player.reference';
import { PLAYERS_REFERENCE } from './players.reference.constants';
import { emitStreamError } from './stream-error.utils';

declare const RTSPtoWEBPlayer: any;

export class WebSocketPlayerReference extends SnVideoPlayerReference {
  public webPlayerRef;

  private _playPromise: Promise<void>;

  private _callback: Function;

  private _isPlaying: boolean;

  private _src: string;

  constructor(
    playerElement: HTMLElement,
    player: HTMLDivElement,
    src: string,
    platform: Platform,
    state$: BehaviorSubject<SnPlayerState>,
    currentTime$: BehaviorSubject<number>,
    bufferTime$: BehaviorSubject<number>,
    totalTime$: BehaviorSubject<number>,
    volume$: BehaviorSubject<number>,
    played$: BehaviorSubject<any>,
    loaded$: Subject<any>,
    ended$: Subject<void>,
    autoplay: boolean,
    callback: Function,
    private readonly _datadogService: FsDataDogRumService,
    private readonly camId: string,
  ) {
    super(
      SnVideoPlayerType.WEBSOCKET,
      playerElement,
      platform,
      state$,
      currentTime$,
      bufferTime$,
      totalTime$,
      volume$,
      played$,
      loaded$,
      ended$,
    );
    this._callback = callback;
    this._src = src;
    this._init(player, src, autoplay);
  }

  public play(): void {
    this._isPlaying = true;
    this._playPromise = this.videoElement.play();
  }

  public pause(): void {
    this._isPlaying = false;
    const playPromise = this._playPromise;
    if (playPromise !== undefined) {
      playPromise.then(() => {
        this.videoElement.pause();
        if (this._playPromise === playPromise) {
          this._playPromise = undefined;
        }
      });
    } else {
      this.videoElement.pause();
    }
  }

  public destroy(): void {
    this.videoElement.pause();
    this.videoElement.removeAttribute('src');
    this.videoElement.load();
    this.webPlayerRef?.destroy();
  }

  private _init(element: HTMLDivElement, src: string, autoplay: boolean): void {
    const options = {
      parentElement: element,
      autoplay,
      controls: false,
      timeout: 5,
    };
    this.webPlayerRef = new RTSPtoWEBPlayer(options);
    this.videoElement = this.webPlayerRef.video;
    this.webPlayerRef.load(src);
    this.videoElement.onerror = () => {
      if (this.videoElement?.error) {
        console.error(this.videoElement.error);
        emitStreamError({
          datadogService: this._datadogService,
          cameraId: this.camId,
          protocol: PLAYERS_REFERENCE.PROTOCOL.WEBSOCKET,
          sourceUi: PLAYERS_REFERENCE.SOURCE_UI.WEBSOCKET,
          errorType: 'video_element_error',
          rawError: this.videoElement.error?.message || 'Video element error',
          errorCode: this.videoElement.error?.code || 'UNKNOWN',
          isLive: this.live,
          isFatal: false,
          streamUrl: this._src,
          protocolSpecificFields: {
            error_source: 'video_element',
            media_error_code:
              typeof this.videoElement.error?.code === 'number'
                ? this.videoElement.error.code
                : undefined,
            media_error_message: this.videoElement.error?.message || 'Video element error',
          },
        });
      }
      this.webPlayerRef.load(src);
    };
    this.webPlayerRef.onerror = (source, error) => {
      console.error(`Error from ${source} with value ${error}`);
      emitStreamError({
        datadogService: this._datadogService,
        cameraId: this.camId,
        protocol: PLAYERS_REFERENCE.PROTOCOL.WEBSOCKET,
        sourceUi: PLAYERS_REFERENCE.SOURCE_UI.WEBSOCKET,
        errorType: 'websocket_error',
        rawError: error?.toString() || JSON.stringify(error),
        errorCode: 'WEBSOCKET_ERROR',
        isLive: this.live,
        isFatal: false,
        streamUrl: this._src,
        protocolSpecificFields: {
          error_source: 'websocket',
        },
      });
    };
    if (autoplay || this._isPlaying) {
      this._playPromise = this.videoElement.play();
      this._playPromise.then(() => this._callback());
    } else {
      this._callback();
    }
    setTimeout(() => {
      this.webPlayerRef.ontimeout = () => {
        emitStreamError({
          datadogService: this._datadogService,
          cameraId: this.camId,
          protocol: PLAYERS_REFERENCE.PROTOCOL.WEBSOCKET,
          sourceUi: PLAYERS_REFERENCE.SOURCE_UI.WEBSOCKET,
          errorType: 'timeout',
          rawError: 'WebSocket connection timeout',
          errorCode: 'TIMEOUT',
          isLive: this.live,
          isFatal: false,
          streamUrl: this._src,
          protocolSpecificFields: {
            error_source: 'timeout',
          },
        });
        this.webPlayerRef.load(src);
        if (!this.loaded) {
          this._callback();
        }
      };
      this.webPlayerRef.onwsclose = (code, reason) => {
        console.error(`Websocket Error: code: ${code} - reason: ${reason}`);
        const isFatal = code !== 1000 && code !== 1006;
        emitStreamError({
          datadogService: this._datadogService,
          cameraId: this.camId,
          protocol: PLAYERS_REFERENCE.PROTOCOL.WEBSOCKET,
          sourceUi: PLAYERS_REFERENCE.SOURCE_UI.WEBSOCKET,
          errorType: 'websocket_close',
          rawError: `WebSocket closed with code: ${code}, reason: ${reason}`,
          errorCode: code,
          isLive: this.live,
          isFatal,
          streamUrl: this._src,
          protocolSpecificFields: {
            error_source: 'websocket',
            websocket_close_code: code,
            websocket_close_reason: reason,
          },
        });
        switch (code) {
          case 1000:
          case 1006:
            this.webPlayerRef.load(src);
            break;
          default:
            this.webPlayerRef.destroy();
        }
      };
    }, 1000);
  }
}
